import {invoke, isTauri} from '@tauri-apps/api/core';
import {
  currentMonitor,
  getCurrentWindow,
  LogicalSize,
  PhysicalSize,
  Window,
} from "@tauri-apps/api/window";
import {setupForwardingConsole} from './forward-logs';

function isDesktopRuntime(): boolean {
  try {
    return isTauri();
  } catch {
    return false;
  }
}

let isNative: boolean = isDesktopRuntime();
let currentWindow: Window | null = null;

let isMain: boolean = false;
let isGrindMode: boolean = false;
let isNotification: boolean = false;

const OS_HANDLED_PROTOCOLS = ["mailto:", "tel:"];

function toAbsoluteUrl(url: string): string {
  try {
    return new URL(url, window.location.href).href;
  } catch {
    return url;
  }
}

function externalUrlFromAnchor(anchor: HTMLAnchorElement): string | null {
  const href = anchor.getAttribute("href");
  if (!href) {
    return null;
  }

  let resolved: URL;
  try {
    resolved = new URL(href, window.location.href);
  } catch {
    return null;
  }

  if (OS_HANDLED_PROTOCOLS.includes(resolved.protocol)) {
    return resolved.href;
  }

  const isHttp = resolved.protocol === "http:" || resolved.protocol === "https:";
  if (!isHttp || resolved.origin === window.location.origin) {
    return null;
  }

  return resolved.href;
}

function handleAnchorClick(event: MouseEvent) {
  if (event.defaultPrevented || event.button !== 0) {
    return;
  }

  const target = event.target as Element | null;
  const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
  if (!anchor) {
    return;
  }

  const url = externalUrlFromAnchor(anchor);
  if (!url) {
    return;
  }

  event.preventDefault();
  void Native.openExternal(url);
}

export namespace Native {
  interface SaveDialogFilter {
    name: string;
    extensions: string[];
  }

  export function IsNative() {
    return isNative;
  }

  export function IsMainWindow() {
    return isMain;
  }

  export function IsGrindMode() {
    return isGrindMode;
  }

  export function IsNotificationWindow() {
    return isNotification;
  }

  export async function nativeSetup(): Promise<void> {
    isNative = isTauri();
    if (!isNative) {
      return;
    }

    await setupForwardingConsole();

    currentWindow = getCurrentWindow();
    const label = currentWindow.label;
    isMain = label === "main";
    isGrindMode = label === "grind_mode";
    isNotification = label === "notification";

    document.addEventListener("click", handleAnchorClick);
  }

  export function setDecoration(enabled: boolean) {
    currentWindow?.setDecorations(enabled);
  }

  export function setWindowSize(width: number, height: number) {
    currentWindow?.setSize(new LogicalSize(width, height));
  }

  export async function getMonitorSize(): Promise<PhysicalSize> {
    const mon = await currentMonitor();
    if (mon == null) {
      throw "no monitor detected";
    }
    return mon.size;
  }

  export function hideCurrentWindow() {
    currentWindow?.hide();
  }

  export function showCurrentWindow() {
    currentWindow?.show();
  }

  export async function showGrindMode() {
    await invoke("show_grind_mode_window")
  }

  export async function openExternal(url: string): Promise<void> {
    if (!url) {
      return;
    }

    if (!isNative) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    // The Tauri webview silently drops `window.open`/`target="_blank"`, so the
    // URL has to be handed over to the OS default browser through the backend.
    try {
      await invoke("open_external", { url: toAbsoluteUrl(url) });
    } catch (e) {
      console.error("open_external failed:", e);
    }
  }

  export async function saveBytesToUserPath(
    bytes: Uint8Array,
    suggestedFileName: string,
    filters?: SaveDialogFilter[]
  ): Promise<boolean> {
    if (!isNative) {
      return false;
    }

    try {
      const filterName = filters?.[0]?.name ?? null;
      const filterExtensions = filters?.[0]?.extensions ?? null;

      await invoke("file_save_bytes", {
        suggestedName: suggestedFileName,
        contents: Array.from(bytes),
        filterName,
        filterExtensions,
      });

      return true;
    } catch (e: any) {
      // "cancelled" is a normal user cancel, not a real error
      if (e !== "cancelled" && !String(e).includes("cancelled")) {
        console.error("file_save_bytes failed:", e);
      }
      return false;
    }
  }

  export async function saveTextToUserPath(
    content: string,
    suggestedFileName: string,
    filters?: SaveDialogFilter[]
  ): Promise<boolean> {
    const encoder = new TextEncoder();
    return await saveBytesToUserPath(
      encoder.encode(content),
      suggestedFileName,
      filters
    );
  }
}
